import type { CoreError } from '@ingenico/api-sdk-core/common';
import type { DeleteConfigurationByIdV1MutationResponseApi } from '@ingenico/api-sdk-device-management-private';
import type { ConfigurationResourceListResponseApi } from '@ingenico/api-sdk-device-management-public';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_CONFIGURATION_PROFILE } from '@ingenico/util-i18n-config';
import { Level, useShowNotification } from '@ingenico/react-notifications';
import type { BusinessServiceRequest } from '@ingenico/react-section-utils';
import { useBusinessService } from '@ingenico/react-section-utils';
import { useDeleteModalConfirmation } from '@ingenico/react-ui/hooks/use-delete-modal-confirmation';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';

export function useDeleteConfigurationProfile({
  deletedId,
  configurationListQuery,
  resetDataGridSearchbarQuery,
}: {
  readonly deletedId: string;
  readonly configurationListQuery: BusinessServiceRequest<ConfigurationResourceListResponseApi>;
  readonly resetDataGridSearchbarQuery: () => void;
}) {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_CONFIGURATION_PROFILE));
  const deviceManagementService = useBusinessService(DeviceManagementService);
  const [deleteConfigurationProfileRequestState, setDeleteConfigurationProfileRequestState] = useAsyncDataState<
    DeleteConfigurationByIdV1MutationResponseApi,
    CoreError
  >();

  const showNotification = useShowNotification();

  const handleDeleteConfigurationProfile = useCallback(
    async (configurationId: string) => {
      await new Promise((resolve, reject) => {
        requestAndUpdateState(
          deviceManagementService.deleteConfigurationProfile(configurationId),
          setDeleteConfigurationProfileRequestState,
          {
            onSuccess: () => {
              configurationListQuery?.refetch();
              resetDataGridSearchbarQuery();

              showNotification({ level: Level.Success, content: t('success.default.delete') });
              resolve(void 0);
            },
            onError: (error) => {
              resetDataGridSearchbarQuery();

              showNotification({ level: Level.Error, content: t('error.default.delete') });

              reject(error);
            },
          },
        );
      });
    },
    [
      configurationListQuery,
      deviceManagementService,
      setDeleteConfigurationProfileRequestState,
      showNotification,
      t,
      resetDataGridSearchbarQuery,
    ],
  );

  const handleDeleteAction: () => Promise<void> = useCallback(
    () => handleDeleteConfigurationProfile(deletedId),
    [deletedId, handleDeleteConfigurationProfile],
  );

  const { handleToggleDeleteDialog, deleteConfirmationComponentChildren } = useDeleteModalConfirmation({
    deleteAction: handleDeleteAction,
    i18nNamespaces: [ALL_LNGS_NAMESPACES.COMMON],
    isDeleteLoading: deleteConfigurationProfileRequestState.isLoading || configurationListQuery?.isLoading,
    confirmLabelKey: `button.confirm`,
    hideNotifications: true,
    titleKey: t('confirmation.delete.title'),
  });

  return { handleToggleDeleteDialog, deleteConfirmationComponentChildren };
}
